import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useInitData, useTranslation } from '@/contexts';
import { logoutWebAdmin } from '@/features/auth/logoutWebAdmin';
import { ApiError, updateOwnProfile } from '@/services/api';
import styles from './ProfileCompletionGate.module.css';

/**
 * Пока имя и фамилия не заполнены, кабинет закрыт: вместо него форма
 * с двумя полями. Данные приходят из `me` в /init; после сохранения
 * /init перезапрашивается и, если поля заполнены, гейт пропускает дальше.
 */
export function ProfileCompletionGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { data, status, reload } = useInitData();

  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Ждём ответа /init после сохранения: пока он не пришёл, судить рано.
  const [awaitingInit, setAwaitingInit] = useState(false);

  useEffect(() => {
    if (!awaitingInit || status !== 'ready') return;

    setAwaitingInit(false);

    if (!data?.identity.firstName || !data?.identity.lastName) {
      setError(t('profileGate.errorNotSaved'));
    }
  }, [awaitingInit, status, data, t]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setError(null);
      setIsSaving(true);

      try {
        await updateOwnProfile({ name: name.trim(), surname: surname.trim() });
        // Сервер ответил 204 — перепроверяем себя через /init.
        // Если имя так и не появилось, форма покажется снова, уже с объяснением.
        setAwaitingInit(true);
        reload();
      } catch (caught) {
        const code = caught instanceof ApiError ? caught.code : 'INTERNAL';
        setError(
          t(
            code === 'UNAVAILABLE'
              ? 'profileGate.errorUnavailable'
              : code === 'INVALID'
                ? 'profileGate.errorInvalid'
                : 'profileGate.errorInternal',
          ),
        );
      } finally {
        setIsSaving(false);
      }
    },
    [name, surname, reload, t],
  );

  // Пока /init не пришёл, решать нечего — пропускаем прежние гейты вперёд.
  if (status !== 'ready' || !data) {
    return children;
  }

  const { firstName, lastName } = data?.identity ?? { firstName: '', lastName: '' };

  if (status === 'ready' && firstName && lastName) {
    return children;
  }

  const canSubmit = name.trim().length > 0 && surname.trim().length > 0 && !isSaving;

  return (
    <div className={styles.screen}>
      <form className={styles.card} onSubmit={(event) => void handleSubmit(event)}>
        <h1 className={styles.title}>{t('profileGate.title')}</h1>
        <p className={styles.subtitle}>{t('profileGate.subtitle')}</p>

        <label className={styles.label}>
          <span>{t('profileGate.nameLabel')}</span>
          <input
            className={styles.input}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('profileGate.namePlaceholder')}
            autoComplete="given-name"
            disabled={isSaving}
            autoFocus
            required
          />
        </label>

        <label className={styles.label}>
          <span>{t('profileGate.surnameLabel')}</span>
          <input
            className={styles.input}
            value={surname}
            onChange={(event) => setSurname(event.target.value)}
            placeholder={t('profileGate.surnamePlaceholder')}
            autoComplete="family-name"
            disabled={isSaving}
            required
          />
        </label>

        <button className={styles.button} type="submit" disabled={!canSubmit}>
          {isSaving ? t('profileGate.saving') : t('profileGate.submit')}
        </button>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className={styles.signOut}
          onClick={() => void logoutWebAdmin()}
          disabled={isSaving}
        >
          {t('auth.signOut')}
        </button>
      </form>
    </div>
  );
}
